import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "../api";
import { useNotification } from "../context/NotificationContext";

const AdminHome = ({ user, events, loading, fetchEvents }) => {
  const { showToast } = useNotification();
  const navigate = useNavigate();
  const [createLoading, setCreateLoading] = useState(false);
  const [newEvent, setNewEvent] = useState({
    name: "",
    description: "",
    eventDate: "",
    locationName: "",
    locationGoogleMapsLink: "",
    clientName: "",
    eventPhoto: "",
  });
  const [eventPhotoFile, setEventPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEventPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const getPlanLimit = () => {
    const plan = user.plan || 'free';
    if (plan === 'free') return 1;
    if (plan === 'starter') return 1;
    if (plan === 'pro') return 1;
    if (plan === 'corporate') return Infinity;
    return 1;
  };

  const createEvent = async () => {
    if (user.role === 'admin') {
      showToast("الأدمن لا يحتاج لإنشاء مناسبات من هنا.", "info");
      return;
    }

    const limit = getPlanLimit();
    if (events.length >= limit) {
      showToast(`عفواً، لقد وصلت للحد الأقصى لخطة "${user.plan || 'free'}" (${limit} مناسبات). قم بترقية حسابك للمزيد.`, "warning");
      return;
    }

    if (!newEvent.name.trim()) {
      showToast("اكتب اسم الحدث", "warning");
      return;
    }
    if (!eventPhotoFile && !newEvent.eventPhoto) {
      showToast("اختر صورة للحدث", "warning");
      return;
    }

    setCreateLoading(true);
    let uploadedPhotoUrl = newEvent.eventPhoto;

    if (eventPhotoFile) {
      const formData = new FormData();
      formData.append("file", eventPhotoFile);
      formData.append("guestName", "System");
      formData.append("type", "photo");
      // We need eventId but we don't have it yet — use a temp placeholder
      formData.append("eventId", "000000000000000000000000");
      try {
        const res = await api.post("/api/media/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploadedPhotoUrl = res.data.url;
      } catch (err) {
        console.error("Event photo upload error", err);
        showToast("فشل رفع الصورة، حاول مرة أخرى", "danger");
        setCreateLoading(false);
        return;
      }
    }

    try {
      await api.post("/api/events", {
        ...newEvent,
        eventPhoto: uploadedPhotoUrl,
      });
      setNewEvent({
        name: "",
        description: "",
        eventDate: "",
        locationName: "",
        locationGoogleMapsLink: "",
        clientName: "",
        eventPhoto: "",
      });
      setEventPhotoFile(null);
      setPhotoPreview(null);
      fetchEvents(true);
      showToast("تم إنشاء الحدث بنجاح 🎉", "success");
    } catch {
      showToast("فشل إنشاء الحدث", "danger");
    } finally {
      setCreateLoading(false);
    }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm("هل تريد حذف الحدث وكل محتوياته؟")) return;
    try {
      await api.delete(`/api/events/${id}`);
      fetchEvents(true);
      showToast("تم حذف الحدث.", "info");
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="space-y-5"
    >
      {/* Hero */}
      <div className="row g-4 align-items-end mb-4">
        <div className="col-lg-8 text-end">
          <span className="text-primary fw-bold tracking-widest uppercase text-xs">
            لوحة التحكم
          </span>
          <h2
            className="display-5 fw-black text-on-surface mt-1"
            style={{ fontFamily: "var(--font-headline)" }}
          >
            أهلاً بك، {user.username} ✨
          </h2>
          <p className="text-muted fs-5 arabic-leading max-w-xl">
            إدارة مناسباتك السعيدة بقت أسهل بكتير. تابع الصور، الرسايل الصوتية،
            ونظم يومك في مكان واحد.
          </p>
        </div>
        <div className="col-lg-4">
          <div className="bg-white rounded-celebration p-4 p-md-4 shadow-sm border border-light d-flex flex-column h-100">
            <div className="d-flex justify-content-between align-items-start gap-2">
              <div>
                <p className="small fw-bold text-muted mb-1">المناسبات المسجلة</p>
                <h3 className="display-4 fw-black text-primary mb-3">
                  {events.length}
                  {user.role !== 'admin' && (
                    <span className="fs-6 text-muted fw-normal ms-2">
                      / {getPlanLimit() === Infinity ? '∞' : getPlanLimit()}
                    </span>
                  )}
                </h3>
              </div>
              {user.role !== 'admin' && getPlanLimit() !== Infinity && (
                <div className="w-16 h-16 rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                     style={{
                       background: `conic-gradient(var(--primary) ${Math.min((events.length / getPlanLimit()) * 100, 100)}%, var(--primary-dim) 0)`,
                       width: '64px', height: '64px'
                     }}>
                  <div className="w-12 h-12 rounded-circle bg-white d-flex align-items-center justify-content-center text-[10px] fw-black text-primary"
                       style={{ width: '48px', height: '48px' }}>
                    {Math.round(Math.min((events.length / getPlanLimit()) * 100, 100))}%
                  </div>
                </div>
              )}
            </div>
            <div className="mt-auto pt-3 border-top d-flex justify-content-between align-items-center">
              <span className="text-xs fw-bold text-muted uppercase">
                نوع الحساب
              </span>
              <div className="d-flex flex-column align-items-end">
                <span
                  className={`badge ${
                    user.role === "admin"
                      ? "bg-primary text-white"
                      : "bg-success-container text-success"
                  } rounded-pill px-3 py-1 mb-1`}
                >
                  {user.role === "admin" ? "مدير النظام" : "منظم مناسبات"}
                </span>
                {user.role !== 'admin' && (
                  <span className="text-[10px] fw-bold text-muted uppercase">
                    خطة: {user.plan === 'free' ? 'المجانية' :
                           user.plan === 'starter' ? 'البداية' :
                           user.plan === 'pro' ? 'الاحترافية' : 'الشركات'}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-4">
        {/* Create Form */}
        {user.role !== 'admin' && (
        <div className="col-xl-5 order-2 order-xl-1">
          <div
            className="bg-white rounded-celebration p-4 p-md-5 shadow-lg border border-light sticky-lg-top"
            style={{ top: "80px" }}
          >
            <div className="d-flex align-items-center gap-3 mb-4">
              <div className="bg-primary-dim p-2 rounded-4">
                <span className="material-symbols-outlined text-primary">
                  celebration
                </span>
              </div>
              <h3 className="h4 fw-black mb-0">ابدأ مناسبة جديدة</h3>
            </div>

            {/* Photo upload with preview */}
            <div className="mb-4">
              <label className="text-xs fw-bold uppercase tracking-wider text-muted mb-2 ms-2 d-block text-end">
                صورة الحدث (البانر) *
              </label>
              <div
                className="position-relative overflow-hidden rounded-4 border-2 border-dashed mb-2"
                style={{
                  height: photoPreview ? "160px" : "100px",
                  borderColor: photoPreview ? "var(--primary)" : "#ddd",
                  background: photoPreview
                    ? "transparent"
                    : "var(--surface-container-low)",
                  transition: "all 0.3s ease",
                }}
              >
                {photoPreview ? (
                  <>
                    <img
                      src={photoPreview}
                      alt="Preview"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                    <div
                      className="position-absolute inset-0 d-flex align-items-center justify-content-center"
                      style={{
                        background: "rgba(0,0,0,0.35)",
                        opacity: 0,
                        transition: "opacity 0.2s",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.opacity = "1")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.opacity = "0")
                      }
                    >
                      <label
                        htmlFor="photo-upload-new"
                        className="btn btn-sm btn-light rounded-pill fw-bold"
                        style={{ cursor: "pointer" }}
                      >
                        تغيير الصورة
                      </label>
                    </div>
                  </>
                ) : (
                  <label
                    htmlFor="photo-upload-new"
                    className="d-flex flex-column align-items-center justify-content-center h-100 w-100"
                    style={{ cursor: "pointer" }}
                  >
                    <span className="material-symbols-outlined text-muted mb-1">
                      add_photo_alternate
                    </span>
                    <span className="text-muted small fw-bold">
                      اضغط لاختيار صورة
                    </span>
                  </label>
                )}
                <input
                  id="photo-upload-new"
                  type="file"
                  accept="image/*"
                  className="d-none"
                  onChange={handlePhotoChange}
                />
              </div>
            </div>

            <div className="space-y-4">
              <div className="form-group text-end">
                <label className="text-xs fw-bold uppercase tracking-wider text-muted mb-1 ms-2">
                  اسم المناسبة *
                </label>
                <input
                  className="form-control form-control-lg bg-light border-0 rounded-4 px-4 py-3 text-sm"
                  placeholder="مثلاً: زفاف سارة وأحمد"
                  value={newEvent.name}
                  onChange={(e) =>
                    setNewEvent({ ...newEvent, name: e.target.value })
                  }
                />
              </div>
              <div className="row g-3">
                <div className="col-md-6 text-end">
                  <label className="text-xs fw-bold uppercase tracking-wider text-muted mb-1 ms-2">
                    اسم العميل
                  </label>
                  <input
                    className="form-control bg-light border-0 rounded-4 px-4 py-3 text-sm"
                    placeholder="الاسم الثنائي"
                    value={newEvent.clientName}
                    onChange={(e) =>
                      setNewEvent({ ...newEvent, clientName: e.target.value })
                    }
                  />
                </div>
                <div className="col-md-6 text-end">
                  <label className="text-xs fw-bold uppercase tracking-wider text-muted mb-1 ms-2">
                    تاريخ الحدث
                  </label>
                  <input
                    type="date"
                    className="form-control bg-light border-0 rounded-4 px-4 py-3 text-sm"
                    value={newEvent.eventDate}
                    onChange={(e) =>
                      setNewEvent({ ...newEvent, eventDate: e.target.value })
                    }
                  />
                </div>
              </div>
              <div className="form-group text-end">
                <label className="text-xs fw-bold uppercase tracking-wider text-muted mb-1 ms-2">
                  وصف المناسبة
                </label>
                <textarea
                  className="form-control bg-light border-0 rounded-4 px-4 py-3 text-sm"
                  placeholder="اكتب كلمة بسيطة عن المناسبة..."
                  rows="2"
                  value={newEvent.description}
                  onChange={(e) =>
                    setNewEvent({ ...newEvent, description: e.target.value })
                  }
                />
              </div>
              <div className="form-group text-end">
                <label className="text-xs fw-bold uppercase tracking-wider text-muted mb-1 ms-2">
                  اسم المكان
                </label>
                <input
                  className="form-control bg-light border-0 rounded-4 px-4 py-3 text-sm"
                  placeholder="اسم القاعة أو الفندق"
                  value={newEvent.locationName}
                  onChange={(e) =>
                    setNewEvent({ ...newEvent, locationName: e.target.value })
                  }
                />
              </div>
              <div className="form-group text-end">
                <label className="text-xs fw-bold uppercase tracking-wider text-muted mb-1 ms-2">
                  رابط الخريطة
                </label>
                <div className="position-relative">
                  <span className="material-symbols-outlined position-absolute top-50 translate-middle-y start-0 ms-3 text-muted fs-6">
                    map
                  </span>
                  <input
                    className="form-control bg-light border-0 rounded-4 ps-5 pe-4 py-3 text-sm"
                    placeholder="Paste Google Maps URL"
                    value={newEvent.locationGoogleMapsLink}
                    onChange={(e) =>
                      setNewEvent({
                        ...newEvent,
                        locationGoogleMapsLink: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
              <button
                className="btn btn-primary w-100 py-3 rounded-pill fw-bold shadow-lg mt-3 d-flex align-items-center justify-content-center gap-2"
                onClick={createEvent}
                disabled={createLoading}
              >
                {createLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    />
                    جاري الإنشاء...
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined fs-5">
                      auto_awesome
                    </span>
                    تفعيل مساحة المناسبة
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
        )}

        {/* Events List */}
        <div className={`${user.role === 'admin' ? 'col-12' : 'col-xl-7'} order-1 order-xl-2 space-y-4`}>
          <div className="d-flex align-items-center justify-content-between mb-4">
            <h3 className="h4 fw-black mb-0 d-flex align-items-center gap-2">
              أعمالك السابقة
              <span className="badge bg-secondary-container text-primary rounded-pill px-3">
                {events.length}
              </span>
            </h3>
          </div>

          <div className="space-y-4">
            {loading && events.length === 0 ? (
              [1, 2].map((n) => (
                <div
                  key={n}
                  className="bg-white rounded-celebration shadow-sm border border-light animate-pulse"
                  style={{ height: "200px" }}
                />
              ))
            ) : events.length === 0 ? (
              <div className="text-center py-5 bg-white rounded-celebration border border-dashed border-primary-dim">
                <div className="display-3 mb-3">🎉</div>
                <p className="text-muted fw-bold mb-0 fs-5">
                  ابدأ مناسبتك الأولى دلوقتي!
                </p>
              </div>
            ) : (
              events.map((ev) => (
                <motion.div
                  key={ev._id}
                  className="card overflow-hidden hover:shadow-lg transition-soft border-0 shadow-sm rounded-celebration group"
                  style={{ minHeight: "180px" }}
                  whileHover={{ y: -3 }}
                >
                  <div className="d-flex flex-column flex-md-row">
                    {/* Event Photo Banner */}
                    <div
                      className="position-relative overflow-hidden flex-shrink-0"
                      style={{
                        width: "100%",
                        maxWidth: "220px",
                        minHeight: "180px",
                      }}
                    >
                      {ev.eventPhoto ? (
                        <>
                          {/* Blurred bg */}
                          <div
                            style={{
                              position: "absolute",
                              inset: 0,
                              backgroundImage: `url(${ev.eventPhoto})`,
                              backgroundSize: "cover",
                              backgroundPosition: "center",
                              filter: "blur(8px) brightness(0.5)",
                              transform: "scale(1.1)",
                            }}
                          />
                          {/* Sharp centered image */}
                          <div className="position-absolute inset-0 d-flex align-items-center justify-content-center p-3">
                            <img
                              src={ev.eventPhoto}
                              alt={ev.name}
                              className="rounded-3 shadow-lg transition-soft group-hover:scale-110"
                              style={{
                                maxWidth: "100%",
                                maxHeight: "140px",
                                objectFit: "cover",
                                border: "2px solid rgba(255,255,255,0.5)",
                              }}
                            />
                          </div>
                        </>
                      ) : (
                        <div
                          className="w-100 h-100 d-flex align-items-center justify-content-center bg-primary-dim"
                          style={{ minHeight: "180px" }}
                        >
                          <span className="material-symbols-outlined text-primary display-3 opacity-50">
                            celebration
                          </span>
                        </div>
                      )}
                      {/* Status badge */}
                      <div className="position-absolute top-0 start-0 p-2">
                        <span
                          className={`badge rounded-pill small shadow-sm ${
                            ev.isApproved
                              ? "bg-success text-white"
                              : "bg-warning text-dark"
                          }`}
                        >
                          {ev.isApproved ? "نشط ✅" : "قيد المراجعة ⏳"}
                        </span>
                      </div>
                    </div>

                    {/* Event Info */}
                    <div className="flex-grow-1 p-3 p-md-4 d-flex flex-column justify-content-between text-end">
                      <div>
                        <div className="d-flex justify-content-between align-items-start mb-1 gap-2">
                          <div className="d-flex gap-1">
                            <button
                              className="btn btn-light btn-sm rounded-pill p-2 border-0"
                              onClick={() => deleteEvent(ev._id || ev.id)}
                              title="حذف"
                            >
                              <span className="material-symbols-outlined fs-6 text-danger">
                                delete
                              </span>
                            </button>
                            <button
                              className="btn btn-light btn-sm rounded-pill p-2 border-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                const url = `${window.location.origin}/event/${ev._id || ev.id}`;
                                navigator.clipboard.writeText(url);
                                showToast("تم نسخ رابط الضيوف!", "success");
                              }}
                              title="نسخ الرابط"
                            >
                              <span className="material-symbols-outlined fs-6 text-primary">
                                content_copy
                              </span>
                            </button>
                          </div>
                          <h4 className="h5 fw-black text-primary mb-0 text-wrap text-end">
                            {ev.name}
                          </h4>
                        </div>

                        {ev.description && (
                          <p
                            className="text-muted small mb-2 text-end"
                            style={{
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {ev.description}
                          </p>
                        )}

                        <div className="d-flex gap-2 gap-md-3 mt-2 text-muted small fw-bold flex-row-reverse flex-wrap">
                          <div className="d-flex align-items-center gap-1">
                            <span className="material-symbols-outlined fs-6">
                              calendar_month
                            </span>
                            {new Date(
                              ev.eventDate || ev.createdAt
                            ).toLocaleDateString("ar-EG")}
                          </div>
                          {ev.locationName && (
                            <div className="d-flex align-items-center gap-1">
                              <span className="material-symbols-outlined fs-6">
                                location_on
                              </span>
                              {ev.locationName}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="mt-3 pt-3 border-top d-flex justify-content-between align-items-center flex-row-reverse">
                        <div className="d-flex align-items-center gap-2">
                          {ev.clientName && (
                            <div className="badge bg-secondary-container text-primary rounded-pill px-3 py-1 text-xs">
                              {ev.clientName}
                            </div>
                          )}
                        </div>
                        <button
                          className="btn btn-link text-primary fw-black text-decoration-none d-flex align-items-center gap-1 p-0 small"
                          onClick={() =>
                            navigate(`${user.role === 'admin' ? '/admin' : '/client'}/event/${ev._id || ev.id}`)
                          }
                        >
                          إدارة المناسبة
                          <span className="material-symbols-outlined fs-6">
                            arrow_left
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </div>
      </div>

      <style>{`
        .inset-0 { top: 0; right: 0; bottom: 0; left: 0; }
        @keyframes pulse {
          0% { opacity: 0.5; }
          50% { opacity: 0.8; }
          100% { opacity: 0.5; }
        }
        .animate-pulse { animation: pulse 1.5s infinite ease-in-out; }
      `}</style>
    </motion.div>
  );
};

export default AdminHome;
